import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, Mail, Calendar, ChevronRight, ArrowLeft, TrendingUp } from 'lucide-react'
import { getNewsletterEditions, formatNewsletterDate } from '../utils/newsletter'

export default function NewsletterPage() {
    const [newsletters, setNewsletters] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        async function fetchNewsletters() {
            const data = await getNewsletterEditions(20)
            setNewsletters(data)
            setLoading(false)
        }
        fetchNewsletters()
    }, [])

    return (
        <div className="min-h-screen bg-slate-950">
            {/* Header */}
            <header className="border-b border-white/10 px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-bold text-white">InkSpot</span>
                    </Link>
                    <Link to="/" className="btn-ghost text-sm">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-6 py-12">
                {/* Hero */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
                        <Mail className="w-4 h-4 text-primary-400" />
                        <span className="text-sm font-medium text-white/80">Newsletter Archive</span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                        <span className="gradient-text">Weekly Writing</span>{' '}
                        <span className="text-white">Insights</span>
                    </h1>
                    <p className="text-lg text-white/60 max-w-2xl mx-auto">
                        Tips, tricks, and updates to help you write better. Published every weekend.
                    </p>
                </div>

                {/* Newsletter List */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-10 h-10 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : newsletters.length === 0 ? (
                    <div className="text-center py-20">
                        <Mail className="w-16 h-16 text-white/20 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">No newsletters yet</h3>
                        <p className="text-white/50 mb-6">Our first newsletter is coming soon!</p>
                        <Link to="/#newsletter" className="btn-primary">
                            Subscribe for Updates
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {newsletters.map((newsletter) => (
                            <Link
                                key={newsletter.id}
                                to={`/newsletter/${newsletter.id}`}
                                className="glass-card p-6 block hover:bg-white/5 transition-all group"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-xl font-semibold text-white mb-2 group-hover:text-primary-400 transition-colors">
                                            {newsletter.title}
                                        </h2>
                                        <p className="text-white/60 text-sm mb-3 line-clamp-2">
                                            {newsletter.content_preview}
                                        </p>
                                        <div className="flex items-center gap-4 text-sm text-white/40">
                                            <span className="flex items-center gap-1.5">
                                                <Calendar className="w-4 h-4" />
                                                {formatNewsletterDate(newsletter.published_at)}
                                            </span>
                                            {newsletter.stats_summary && (
                                                <span className="flex items-center gap-1.5">
                                                    <TrendingUp className="w-4 h-4" />
                                                    {newsletter.stats_summary.totalWordsAnalyzed?.toLocaleString() || 0} words analyzed
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-primary-400 transition-colors flex-shrink-0 mt-1" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {/* Subscribe CTA */}
                <div className="mt-16 glass-card p-8 text-center">
                    <h3 className="text-2xl font-bold text-white mb-3">Never Miss an Issue</h3>
                    <p className="text-white/60 mb-6">
                        Get weekly writing tips and exclusive updates delivered to your inbox.
                    </p>
                    <Link to="/#newsletter" className="btn-primary">
                        <Mail className="w-5 h-5 mr-2" />
                        Subscribe Now
                    </Link>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/10 py-8 mt-12">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <p className="text-white/40 text-sm">
                        © {new Date().getFullYear()} InkSpot. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    )
}
